import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { interval } from 'rxjs';
import { ChartModule } from 'primeng/chart';
import { TableModule } from 'primeng/table';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { DatePickerModule } from 'primeng/datepicker';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { SkeletonModule } from 'primeng/skeleton';
import { ProgressBarModule } from 'primeng/progressbar';
import { DividerModule } from 'primeng/divider';
import { BadgeModule } from 'primeng/badge';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../core/services/auth.service';
import {
  DashboardService,
  DashboardStatsDto,
  DashboardFilter,
  SectionStatus,
} from './dashboard.service';
import { StoreLiteDto } from '../../core/models/store.models';
import { storePicker } from '../../core/utils/store-picker';
import { RevenueTrendItem, CategoryAnalyticsItem } from '../../core/models/product.models';
import { OrderDto } from '../../core/models/order.models';
import { MoneyFixedPipe, MoneyPipe } from '../../shared/pipes/money.pipe';
import { CurrencyService } from '../../core/services/currency.service';

// Turli rangli kategoriya palitrasi (faqat yashil emas — har xil tus)
const PALETTE = [
  '#22c55e', // green
  '#6366f1', // indigo
  '#f97316', // orange
  '#06b6d4', // cyan
  '#eab308', // amber
  '#ec4899', // pink
  '#8b5cf6', // violet
  '#14b8a6', // teal
  '#ef4444', // red
  '#84cc16', // lime
  '#0ea5e9', // sky
  '#f43f5e', // rose
];

const AUTO_REFRESH_MS = 60_000;
const CATEGORY_DONUT_LIMIT = 7;

const EMPTY_STATS: DashboardStatsDto = {
  todaySales: 0,
  todayOrders: 0,
  todayProfit: 0,
  currency: '',
  availableCurrencies: [],
  salesByCurrency: [],
  profitByCurrency: [],
  totalSales: 0,
  totalOrders: 0,
  totalProfit: 0,
  activeProducts: 0,
  previousSales: 0,
  previousOrders: 0,
  previousProfit: 0,
  totalOutstandingDebt: 0,
  debtOrderCount: 0,
  weeklyChart: [],
  topProducts: [],
  lowStockAlerts: [],
  belowCostAlerts: [],
  paymentBreakdown: [],
  hourlySales: [],
  topDebtors: [],
  cashierPerformance: [],
  totalExpenses: 0,
  expenseCount: 0,
  writeOffLoss: 0,
  writeOffCount: 0,
  netProfit: 0,
  previousExpenses: 0,
  previousWriteOffLoss: 0,
  expenseBreakdown: [],
};

function startOfMonth(): Date {
  const d = new Date();
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
}

interface Delta {
  pct: number;
  dir: 'up' | 'down' | 'flat';
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    MoneyPipe,
    CommonModule,
    FormsModule,
    DecimalPipe,
    ChartModule,
    TableModule,
    SelectModule,
    MultiSelectModule,
    DatePickerModule,
    ButtonModule,
    TagModule,
    SkeletonModule,
    ProgressBarModule,
    DividerModule,
    BadgeModule,
    ToggleSwitchModule,
    TooltipModule,
    TranslatePipe,
    MoneyFixedPipe,
  ],
  templateUrl: './dashboard.component.html',
})
export class DashboardComponent implements OnInit {
  private svc = inject(DashboardService);
  private authSvc = inject(AuthService);
  private i18n = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  readonly currencySvc = inject(CurrencyService);

  // ── Filter ────────────────────────────────────────────────────────────────────
  filterFrom: Date = startOfMonth();
  filterTo: Date = new Date();
  selectedStoreIds: string[] = [];

  stores = signal<StoreLiteDto[]>([]);

  /** Bitta do'konli biznesda filtr yashiriladi — `storePicker` ga qarang. */
  readonly storePick = storePicker(this.stores);

  /**
   * Dashboard qaysi valyutadagi savdoni ko'rsatayotgani. Bo'sh — backend tenant
   * kotirovka valyutasini oladi. Barcha raqamlar BITTA valyutada bo'lishi shart:
   * turli valyutadagi summalarni qo'shib bo'lmaydi.
   */
  selectedCurrency = signal<string>('');
  autoRefresh = signal(false);

  get autoRefreshModel(): boolean {
    return this.autoRefresh();
  }
  set autoRefreshModel(v: boolean) {
    this.autoRefresh.set(v);
  }

  // ── State ──────────────────────────────────────────────────────────────────
  loading = signal(true);
  stats = signal<DashboardStatsDto>(EMPTY_STATS);
  trend = signal<RevenueTrendItem[]>([]);
  cats = signal<CategoryAnalyticsItem[]>([]);
  orders = signal<OrderDto[]>([]);

  // Bo'lim holatlari (ok / forbidden / error)
  statStatus = signal<SectionStatus>('ok');
  trendStatus = signal<SectionStatus>('ok');
  catStatus = signal<SectionStatus>('ok');
  orderStatus = signal<SectionStatus>('ok');

  // ── Computed KPIs ───────────────────────────────────────────────────────────
  profitMargin = computed(() => {
    const s = this.stats();
    return s.totalSales > 0 ? (s.totalProfit / s.totalSales) * 100 : 0;
  });

  avgOrderValue = computed(() => {
    const s = this.stats();
    return s.totalOrders > 0 ? s.totalSales / s.totalOrders : 0;
  });

  hasComparison = computed(() => {
    const s = this.stats();
    return s.previousSales > 0 || s.previousOrders > 0 || s.previousProfit > 0;
  });

  salesDelta = computed(() => this.delta(this.stats().totalSales, this.stats().previousSales));
  profitDelta = computed(() => this.delta(this.stats().totalProfit, this.stats().previousProfit));
  ordersDelta = computed(() => this.delta(this.stats().totalOrders, this.stats().previousOrders));

  private delta(curr: number, prev: number): Delta | null {
    if (prev <= 0) return null;
    const pct = ((curr - prev) / prev) * 100;
    return { pct, dir: pct > 0.05 ? 'up' : pct < -0.05 ? 'down' : 'flat' };
  }

  expenseDelta = computed(() =>
    this.delta(this.stats().totalExpenses, this.stats().previousExpenses),
  );

  netProfitDelta = computed<Delta | null>(() => {
    const s = this.stats();
    const prevNet = s.previousProfit - s.previousExpenses - s.previousWriteOffLoss;
    if (!this.hasComparison() || prevNet === 0) return null;
    const pct = ((s.netProfit - prevNet) / Math.abs(prevNet)) * 100;
    return { pct, dir: pct > 0.05 ? 'up' : pct < -0.05 ? 'down' : 'flat' };
  });

  getExpenseLabel(cat: string): string {
    return this.i18n.instant('DASHBOARD.EXPENSES.CAT.' + (cat || 'Other').toUpperCase());
  }

  // ── Chart: Weekly Bar ───────────────────────────────────────────────────────
  weeklyChartData = computed(() => {
    const w = this.stats().weeklyChart;
    return {
      labels: w.map((d) => d.date),
      datasets: [
        {
          label: this.i18n.instant('DASHBOARD.CHARTS.REVENUE'),
          data: w.map((d) => d.total),
          backgroundColor: 'rgba(34,197,94,0.75)',
          borderColor: '#22c55e',
          borderRadius: 5,
          borderWidth: 1,
          maxBarThickness: 40,
          yAxisID: 'y',
        },
        {
          label: this.i18n.instant('DASHBOARD.CHARTS.ORDERS'),
          data: w.map((d) => d.orders),
          backgroundColor: 'rgba(99,102,241,0.75)',
          borderColor: '#6366f1',
          borderRadius: 5,
          borderWidth: 1,
          maxBarThickness: 40,
          yAxisID: 'y1',
        },
      ],
    };
  });

  weeklyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: window.innerWidth < 768 ? 1.6 : 1.2,
    interaction: { mode: 'index' as const, intersect: false },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: { padding: 8, font: { size: 11 }, boxWidth: 12 },
      },
    },
    scales: {
      y: {
        position: 'left' as const,
        beginAtZero: true,
        ticks: { callback: (v: number) => this.fmtM(v), font: { size: 10 }, maxTicksLimit: 5 },
        grid: { color: 'rgba(0,0,0,0.05)' },
      },
      y1: {
        position: 'right' as const,
        beginAtZero: true,
        min: 0,
        suggestedMax: 5,
        grid: { drawOnChartArea: false },
        ticks: {
          precision: 0,
          font: { size: 10 },
          maxTicksLimit: 5,
          callback: (v: number) => (Number.isInteger(v) ? v : ''),
        },
      },
      x: {
        grid: { display: false },
        ticks: { font: { size: 10 } },
      },
    },
  };

  // ── Chart: Revenue + Profit Trend ───────────────────────────────────────────
  trendChartData = computed(() => {
    const t = this.trend();
    return {
      labels: t.map((d) => `${d.monthName} '${String(d.year).slice(2)}`),
      datasets: [
        {
          label: this.i18n.instant('DASHBOARD.CHARTS.REVENUE'),
          data: t.map((d) => d.revenue),
          borderColor: '#16a34a',
          backgroundColor: 'rgba(34,197,94,0.10)',
          fill: true,
          tension: 0.4,
          pointRadius: 3,
          pointBackgroundColor: '#16a34a',
        },
        {
          label: this.i18n.instant('DASHBOARD.CHARTS.PROFIT'),
          data: t.map((d) => d.profit),
          borderColor: '#6366f1',
          backgroundColor: 'rgba(99,102,241,0.06)',
          fill: true,
          tension: 0.4,
          pointRadius: 3,
          pointBackgroundColor: '#6366f1',
        },
      ],
    };
  });

  trendChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: window.innerWidth < 768 ? 1.7 : 1.2,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'top', labels: { padding: 8, font: { size: 11 }, boxWidth: 12 } },
    },
    scales: {
      y: {
        ticks: { callback: (v: number) => this.fmtM(v), font: { size: 10 }, maxTicksLimit: 5 },
        grid: { color: 'rgba(0,0,0,0.05)' },
      },
      x: { grid: { display: false }, ticks: { font: { size: 10 } } },
    },
  };

  // ── Chart: Category Donut (top N + "Boshqa") ────────────────────────────────
  displayCats = computed<CategoryAnalyticsItem[]>(() => {
    const c = [...this.cats()].sort((a, b) => b.revenue - a.revenue);
    if (c.length <= CATEGORY_DONUT_LIMIT) return c;
    const head = c.slice(0, CATEGORY_DONUT_LIMIT);
    const rest = c.slice(CATEGORY_DONUT_LIMIT);
    head.push({
      categoryId: null,
      categoryName: this.i18n.instant('DASHBOARD.CHARTS.OTHER'),
      revenue: rest.reduce((s, x) => s + x.revenue, 0),
      itemsSold: rest.reduce((s, x) => s + x.itemsSold, 0),
      percentage: rest.reduce((s, x) => s + x.percentage, 0),
    });
    return head;
  });

  categoryChartData = computed(() => {
    const c = this.displayCats();
    return {
      labels: c.map((x) => x.categoryName),
      datasets: [
        {
          data: c.map((x) => x.revenue),
          backgroundColor: PALETTE.slice(0, c.length),
          borderWidth: 2,
          borderColor: '#fff',
          hoverOffset: 8,
        },
      ],
    };
  });

  categoryChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: window.innerWidth < 768 ? 1.6 : 1.2,
    cutout: '65%',
    plugins: {
      legend: {
        position: 'bottom',
        labels: { padding: 8, usePointStyle: true, font: { size: 10 }, boxWidth: 8 },
      },
    },
  };

  // ── Chart: Top Products Horizontal Bar (limited) ────────────────────────────
  topProductsChartData = computed(() => {
    const p = this.stats().topProducts.slice(0, 8);
    return {
      labels: p.map((x) =>
        x.productName.length > 22 ? x.productName.slice(0, 22) + '…' : x.productName,
      ),
      datasets: [
        {
          label: this.i18n.instant('DASHBOARD.CHARTS.REVENUE'),
          data: p.map((x) => x.revenue),
          backgroundColor: 'rgba(34,197,94,0.75)',
          borderColor: '#22c55e',
          borderWidth: 1,
          borderRadius: 4,
        },
      ],
    };
  });

  topProductsChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: window.innerWidth < 768 ? 1.4 : 1.2,
    indexAxis: 'y' as const,
    plugins: { legend: { display: false } },
    scales: {
      x: {
        ticks: { callback: (v: number) => this.fmtM(v), font: { size: 10 }, maxTicksLimit: 5 },
        grid: { color: 'rgba(0,0,0,0.05)' },
      },
      y: { grid: { display: false }, ticks: { font: { size: 10 } } },
    },
  };

  // ── Chart: Payment breakdown (doughnut) ─────────────────────────────────────
  paymentChartData = computed(() => {
    const p = this.stats().paymentBreakdown;
    const colorMap: Record<string, string> = {
      Cash: '#22c55e',
      Card: '#6366f1',
      Debt: '#f59e0b',
    };
    return {
      labels: p.map((x) => this.getPaymentLabel(x.type)),
      datasets: [
        {
          data: p.map((x) => x.amount),
          backgroundColor: p.map((x) => colorMap[x.type] ?? '#94a3b8'),
          borderWidth: 2,
          borderColor: '#fff',
          hoverOffset: 8,
        },
      ],
    };
  });

  paymentChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: window.innerWidth < 768 ? 1.6 : 1.2,
    cutout: '60%',
    plugins: {
      legend: {
        position: 'bottom',
        labels: { padding: 8, usePointStyle: true, font: { size: 10 }, boxWidth: 8 },
      },
    },
  };

  // ── Chart: Peak hours (bar, 24h) ────────────────────────────────────────────
  hourlyChartData = computed(() => {
    const h = this.stats().hourlySales;
    return {
      labels: h.map((x) => `${String(x.hour).padStart(2, '0')}:00`),
      datasets: [
        {
          label: this.i18n.instant('DASHBOARD.CHARTS.REVENUE'),
          data: h.map((x) => x.total),
          backgroundColor: 'rgba(20,184,166,0.7)',
          borderColor: '#14b8a6',
          borderRadius: 4,
          borderWidth: 1,
        },
      ],
    };
  });

  hourlyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: window.innerWidth < 768 ? 1.7 : 1.2,
    plugins: { legend: { display: false } },
    scales: {
      y: {
        ticks: { callback: (v: number) => this.fmtM(v), font: { size: 10 }, maxTicksLimit: 5 },
        grid: { color: 'rgba(0,0,0,0.05)' },
      },
      x: {
        grid: { display: false },
        ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 8, font: { size: 9 } },
      },
    },
  };

  // ── Lifecycle ───────────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.svc
      .getStores()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((storeList) => {
        this.stores.set(storeList);
        if (this.authSvc.user()?.role === 'Employee' && storeList.length > 0) {
          this.selectedStoreIds = [storeList[0].id];
        }
        this.loadData();
      });

    interval(AUTO_REFRESH_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.autoRefresh() && !this.loading()) this.loadData();
      });
  }

  /**
   * Dashboarddagi raqamlar yonida turadigan valyuta belgisi.
   * `fmtM` ixcham formatlagich (K/M) — u faqat sonni beradi, belgi shu yerdan olinadi.
   * Ilgari qattiq yozilgan "so'm" turardi va USD tanlanganda ham o'zgarmasdi.
   */
  currencySymbol = computed(() => this.currencySvc.info(this.stats().currency).symbol);

  /** Valyuta taqsimoti kartochkasi uchun — o'sha valyutadagi yalpi foyda. */
  profitFor(currency: string): number {
    return this.stats().profitByCurrency.find((x) => x.currency === currency)?.amount ?? 0;
  }

  onCurrencyChange(code: string): void {
    if (code === this.selectedCurrency()) return;
    this.selectedCurrency.set(code);
    this.loadData();
  }

  loadData(): void {
    this.loading.set(true);
    const filter: DashboardFilter = {
      from: this.filterFrom,
      to: this.filterTo,
      storeIds: this.selectedStoreIds.length ? this.selectedStoreIds : undefined,
      currency: this.selectedCurrency() || undefined,
    };
    this.svc
      .loadAll(filter)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false)),
      )
      .subscribe((data) => {
        this.stats.set(data.stats);
        // Backend qaysi valyutani qo'llaganini aytadi — tanlagich shunga moslashadi.
        if (data.stats.currency) this.selectedCurrency.set(data.stats.currency);
        this.trend.set(data.trend);
        this.cats.set(data.categories);
        this.orders.set(data.orders);
        this.statStatus.set(data.status.stats);
        this.trendStatus.set(data.status.trend);
        this.catStatus.set(data.status.categories);
        this.orderStatus.set(data.status.orders);
      });
  }

  resetFilter(): void {
    this.filterFrom = startOfMonth();
    this.filterTo = new Date();
    this.selectedStoreIds = [];
    this.loadData();
  }

  // ── CSV export ──────────────────────────────────────────────────────────────
  exportCsv(): void {
    const lines: string[] = [];
    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;

    lines.push(this.i18n.instant('DASHBOARD.RECENT_ORDERS.TITLE'));
    lines.push(['#', "Do'kon", 'Sana', 'Holat', "To'lov", 'Summa'].map(esc).join(','));
    for (const o of this.orders()) {
      lines.push(
        [
          o.receiptNumber,
          o.storeName,
          this.formatDate(o.orderDate),
          this.getOrderLabel(o.status),
          this.getPaymentLabel(o.paymentType),
          Math.round(o.totalAmount),
        ]
          .map(esc)
          .join(','),
      );
    }
    lines.push('');
    lines.push(this.i18n.instant('DASHBOARD.CATEGORY_REPORT.TITLE'));
    lines.push(['Kategoriya', 'Soni', 'Daromad', '%'].map(esc).join(','));
    for (const c of this.cats()) {
      lines.push(
        [c.categoryName, c.itemsSold, Math.round(c.revenue), c.percentage].map(esc).join(','),
      );
    }

    lines.push('');
    lines.push(this.i18n.instant('DASHBOARD.EXPENSES.TITLE'));
    lines.push(['Turi', 'Soni', 'Summa'].map(esc).join(','));
    for (const e of this.stats().expenseBreakdown) {
      lines.push(
        [this.getExpenseLabel(e.category), e.count, Math.round(e.amount)].map(esc).join(','),
      );
    }
    const s = this.stats();
    lines.push('');
    lines.push(
      [this.i18n.instant('DASHBOARD.KPI.GROSS_PROFIT'), Math.round(s.totalProfit)]
        .map(esc)
        .join(','),
    );
    lines.push(
      [this.i18n.instant('DASHBOARD.EXPENSES.TOTAL'), Math.round(s.totalExpenses)]
        .map(esc)
        .join(','),
    );
    lines.push(
      [this.i18n.instant('DASHBOARD.EXPENSES.WRITE_OFF'), Math.round(s.writeOffLoss)]
        .map(esc)
        .join(','),
    );
    lines.push(
      [this.i18n.instant('DASHBOARD.KPI.NET_PROFIT'), Math.round(s.netProfit)].map(esc).join(','),
    );

    const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dashboard_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── Helpers ─────────────────────────────────────────────────────────────────
  fmt(v: number): string {
    return new Intl.NumberFormat('uz-UZ').format(Math.round(v));
  }

  fmtM(v: number): string {
    const a = Math.abs(v);
    if (a >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(1)}B`;
    if (a >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (a >= 1_000) return `${(v / 1_000).toFixed(0)}K`;
    return String(Math.round(v));
  }

  getOrderSeverity(status: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    const m: Record<string, 'success' | 'info' | 'warn' | 'danger'> = {
      Paid: 'success',
      Completed: 'success',
      Processing: 'info',
      PartiallyPaid: 'warn',
      Pending: 'warn',
      Debt: 'warn',
      Cancelled: 'danger',
      Returned: 'danger',
    };
    return m[status] ?? 'secondary';
  }

  getOrderLabel(status: string): string {
    const m: Record<string, string> = {
      Paid: "To'langan",
      Completed: 'Yakunlangan',
      Processing: 'Jarayonda',
      Pending: 'Kutilmoqda',
      PartiallyPaid: 'Qisman',
      Debt: 'Nasiya',
      Cancelled: 'Bekor',
      Returned: 'Qaytarilgan',
    };
    return m[status] ?? status;
  }

  getPaymentLabel(type: string): string {
    return { Cash: 'Naqd', Card: 'Karta', Debt: 'Nasiya' }[type] ?? type;
  }

  formatDate(d: string): string {
    return new Date(d).toLocaleDateString('uz-UZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  getLowStockSeverity(qty: number): 'success' | 'warn' | 'danger' {
    if (qty <= 0) return 'danger';
    if (qty <= 5) return 'danger';
    return 'warn';
  }
}
